import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Icon from 'components/OC/oc-icon';

import { getAttributeKey, getRowReferences } from './acousticSelectorHelpers';
import Theme from '../../../../../themes/index';

const EMPTY_VALUE = '—';
const INSULATION_EXCEPTION_BADGE = 'Fire resistance differs by insulation type - see description';

// An insulation exception's fire rating holds for one insulation only, which the row names.
const insulationExceptionBadge = row =>
  row.insulation_product ? `Fire resistance with ${row.insulation_product}` : INSULATION_EXCEPTION_BADGE;

const TableWrapper = styled.div`
  margin: 8px 0;
  max-width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  font-size: 14px;
  width: 100%;

  th,
  td {
    border-bottom: 1px solid #ddd;
    padding: 6px 12px 6px 0;
    text-align: left;
    vertical-align: top;
  }

  th {
    color: ${Theme.colors.textGrey};
    font-weight: 700;
    white-space: nowrap;
  }
`;

const ExceptionBadge = styled.em`
  color: ${Theme.colors.textGrey};
  display: block;
  font-size: 12px;
`;

const DownloadLink = styled.a`
  color: #ce1126;
  display: inline-block;
  margin-right: 8px;
`;

const AcousticResultsTable = ({ results, rowAttributes }) => {
  const rowAttributeKeys = rowAttributes.map(getAttributeKey);
  const rows = results.map(row => ({ row, references: getRowReferences(row, rowAttributeKeys) }));
  const showDownloads = rows.some(({ references }) => references.some(reference => reference.url));

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            {rowAttributes.map(attribute => (
              <th key={attribute.field}>{attribute.label}</th>
            ))}
            <th>Reference</th>
            {showDownloads && <th>Download</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ row, references }) => (
            <tr key={row.record_id}>
              {rowAttributeKeys.map(key => (
                <td key={key}>
                  {row[key] || EMPTY_VALUE}
                  {/* The exception concerns the fire rating, so it is flagged beside it */}
                  {key === 'fire_rating' && row.insulation_exception && (
                    <ExceptionBadge>{insulationExceptionBadge(row)}</ExceptionBadge>
                  )}
                </td>
              ))}
              <td>{references.map(reference => reference.number).join(' · ')}</td>
              {showDownloads && (
                <td>
                  {references
                    .filter(reference => reference.url)
                    .map(reference => (
                      <DownloadLink
                        key={reference.number}
                        href={reference.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${reference.number} – ${reference.types}`}
                        data-track="file-download"
                        data-track-doc-type="Acoustic Selector Report"
                        data-track-file-name={reference.number}
                      >
                        <Icon type="file-pdf-o" />
                      </DownloadLink>
                    ))}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
};

AcousticResultsTable.propTypes = {
  results: PropTypes.arrayOf(
    PropTypes.shape({
      record_id: PropTypes.string,
      insulation_exception: PropTypes.bool,
      insulation_product: PropTypes.string,
      references: PropTypes.arrayOf(
        PropTypes.shape({
          number: PropTypes.string,
          types: PropTypes.string,
          for: PropTypes.arrayOf(PropTypes.string),
          url: PropTypes.string,
          code_reference: PropTypes.bool,
        })
      ),
    })
  ).isRequired,
  rowAttributes: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      field: PropTypes.string,
    })
  ).isRequired,
};

export default AcousticResultsTable;
